import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Star, Clock, Users, Printer, Check } from 'lucide-react';
import { useState } from 'react';
import { fetchRecipeById } from '@/lib/recipes';
import LoadingSpinner from '@/components/LoadingSpinner';
import styles from './RecipeDetailPage.module.css';

function formatTime(minutes: number): string {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export default function RecipeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [checkedIngredients, setCheckedIngredients] = useState<Set<number>>(new Set());
  const [checkedSteps, setCheckedSteps] = useState<Set<number>>(new Set());

  const { data: recipe, isLoading, error } = useQuery({
    queryKey: ['recipe', id],
    queryFn: () => fetchRecipeById(id!),
    enabled: !!id,
  });

  const toggleIngredient = (index: number) => {
    const newChecked = new Set(checkedIngredients);
    if (newChecked.has(index)) newChecked.delete(index);
    else newChecked.add(index);
    setCheckedIngredients(newChecked);
  };

  const toggleStep = (index: number) => {
    const newChecked = new Set(checkedSteps);
    if (newChecked.has(index)) newChecked.delete(index);
    else newChecked.add(index);
    setCheckedSteps(newChecked);
  };

  const handlePrint = () => window.print();

  if (isLoading) return <LoadingSpinner />;

  if (error || !recipe) {
    return (
      <div className={styles.errorState}>
        <h2>Recipe Not Found</h2>
        <Link to="/recipes" className={styles.backLink}>Return to Recipes</Link>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <Link to="/recipes" className={styles.backButton}>
          <ArrowLeft size={20} />
          Back to Recipes
        </Link>
        <button onClick={handlePrint} className={styles.printButton}>
          <Printer size={20} />
          Print Recipe
        </button>
      </header>

      <div className={styles.content}>
        <div className={styles.heroSection}>
          <img
            src={recipe.image}
            alt={recipe.title}
            className={styles.heroImage}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder-recipe.png';
            }}
          />
          <div className={styles.heroContent}>
            <h1 className={styles.title}>{recipe.title}</h1>
            {recipe.description && <p className={styles.description}>{recipe.description}</p>}
            <div className={styles.meta}>
              {recipe.rating > 0 && (
                <span className={styles.metaItem}>
                  <Star size={20} fill="currentColor" className={styles.starIcon} />
                  {recipe.rating.toFixed(1)} rating
                </span>
              )}
              {(recipe.prepTime > 0 || recipe.cookTime > 0) && (
                <span className={styles.metaItem}>
                  <Clock size={20} />
                  {formatTime(recipe.prepTime + recipe.cookTime)}
                </span>
              )}
              {recipe.servings && recipe.servings > 0 && (
                <span className={styles.metaItem}>
                  <Users size={20} />
                  {recipe.servings} servings
                </span>
              )}
              {recipe.calories > 0 && <span className={styles.metaItem}>{recipe.calories} calories</span>}
            </div>
          </div>
        </div>

        <div className={styles.recipeBody}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Ingredients</h2>
            <ul className={styles.ingredientList}>
              {recipe.ingredients.map((ingredient, index) => (
                <li
                  key={index}
                  className={`${styles.ingredientItem} ${checkedIngredients.has(index) ? styles.checked : ''}`}
                  onClick={() => toggleIngredient(index)}
                >
                  <span className={styles.checkbox}>
                    {checkedIngredients.has(index) && <Check size={16} />}
                  </span>
                  <span>{ingredient}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Instructions</h2>
            <ol className={styles.stepList}>
              {recipe.directions.map((step, index) => (
                <li
                  key={index}
                  className={`${styles.stepItem} ${checkedSteps.has(index) ? styles.checked : ''}`}
                  onClick={() => toggleStep(index)}
                >
                  <span className={styles.stepNumber}>
                    {checkedSteps.has(index) ? <Check size={16} /> : index + 1}
                  </span>
                  <span className={styles.stepText}>{step}</span>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
}
